import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FileText, Shield, Lock, TrendingUp } from 'lucide-react';
import '../styles/jp-morgan.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPageJP = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [valueLock, setValueLock] = useState(null);

  useEffect(() => {
    const fetchValueLock = async () => {
      try {
        const res = await fetch(`${API}/value-lock/summary`);
        if (res.ok) {
          const data = await res.json();
          setValueLock(data);
        }
      } catch (error) {
        console.error('Error fetching value lock:', error);
      }
    };
    fetchValueLock();
  }, []);

  const formatNumber = (num) => {
    if (num >= 1e12) return (num / 1e12).toFixed(1) + 'T';
    if (num >= 1e9) return (num / 1e9).toFixed(0) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #050d18 100%)' }}>
      {/* Navigation */}
      <nav className="border-b" style={{ borderColor: 'rgba(184, 134, 11, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #b8860b 0%, #8b6914 100%)' }}>
              <span className="text-[#0a1628] font-bold text-lg">B</span>
            </div>
            <div>
              <h1 className="text-white text-lg font-light tracking-wide">BETHEREUM</h1>
              <p className="text-xs" style={{ color: '#6c757d', letterSpacing: '0.1em' }}>PRIVATE WEALTH MANAGEMENT</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Subscribe Button */}
            <button
              onClick={() => navigate('/subscribe')}
              className="flex items-center gap-2 px-4 py-2 text-xs font-medium tracking-wider transition-all hover:scale-105"
              style={{ 
                background: 'linear-gradient(180deg, #b8860b 0%, #9a7209 100%)',
                color: '#0a1628',
                border: 'none',
                borderRadius: '2px'
              }}
            >
              SUBSCRIBE
            </button>
            {/* White Paper Button - Prominently Visible */}
            <button
              onClick={() => navigate('/whitepaper')}
              className="flex items-center gap-2 px-4 py-2 text-xs font-medium tracking-wider transition-all hover:scale-105"
              style={{ 
                background: 'linear-gradient(180deg, #1a2d4a 0%, #0d1d35 100%)',
                color: '#b8860b',
                border: '1px solid #b8860b',
                borderRadius: '2px'
              }}
            >
              <FileText size={14} />
              WHITE PAPER
            </button>
            <a href="/compliance" className="text-xs tracking-wider" style={{ color: '#6c757d' }}>COMPLIANCE</a>
            <a href="/investor-guide" className="text-xs tracking-wider" style={{ color: '#6c757d' }}>INVESTOR GUIDE</a>
            {user ? (
              <button
                onClick={() => navigate('/admin-dashboard')}
                className="px-4 py-2 text-xs font-medium tracking-wider"
                style={{ 
                  background: 'linear-gradient(180deg, #b8860b 0%, #9a7209 100%)',
                  color: '#0a1628',
                  border: 'none',
                  borderRadius: '2px'
                }}
              >
                DASHBOARD
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 text-xs font-medium tracking-wider"
                style={{ 
                  background: 'transparent',
                  color: '#b8860b',
                  border: '1px solid #b8860b',
                  borderRadius: '2px'
                }}
              >
                CLIENT LOGIN
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-32 px-6 overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 z-0 opacity-20"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/e8bab4bf-6551-45f6-918c-29c0407b9456/images/21224098d3ae02ba07fefaac3923ffe23c06dd5df1d06e313bcb2541fa33f380.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/80 via-[#0a1628]/90 to-[#050d18] z-0" />
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <p className="text-xs tracking-widest mb-6" style={{ color: '#b8860b' }}>
            PRIVATE CREDIT INFRASTRUCTURE • ACCREDITED INVESTORS ONLY
          </p>
          
          <h1 className="text-5xl md:text-6xl font-light text-white mb-6 tracking-wide" style={{ lineHeight: '1.2' }}>
            Institutional-Grade<br />
            <span style={{ color: '#b8860b' }}>Securities Finance Platform</span>
          </h1>
          
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-12 font-light">
            A permissioned digital credit infrastructure designed for accredited participants. 
            Smart contract transparency. Non-custodial execution. Compliance-first design.
          </p>

          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => navigate('/signup')}
              className="px-8 py-4 text-sm font-medium tracking-wider"
              style={{ 
                background: 'linear-gradient(180deg, #b8860b 0%, #9a7209 100%)',
                color: '#0a1628',
                border: 'none',
                borderRadius: '2px'
              }}
            >
              APPLY FOR ACCESS
            </button>
            <button
              onClick={() => navigate('/whitepaper')}
              className="flex items-center gap-2 px-8 py-4 text-sm font-medium tracking-wider"
              style={{ 
                background: 'transparent',
                color: '#b8860b',
                border: '1px solid rgba(184, 134, 11, 0.3)',
                borderRadius: '2px'
              }}
            >
              <FileText size={16} />
              WHITE PAPER
            </button>
          </div>
        </div>
      </section>

      {/* Value Lock Banner */}
      <section className="py-12 px-6" style={{ background: 'linear-gradient(180deg, rgba(184, 134, 11, 0.1) 0%, transparent 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(184, 134, 11, 0.2)', border: '1px solid rgba(184, 134, 11, 0.3)' }}>
              <Lock size={14} className="text-[#b8860b]" />
              <span className="text-xs tracking-widest text-[#b8860b]">TOTAL VALUE LOCKED</span>
              {valueLock?.is_live ? (
                <span className="ml-2 px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400">LIVE</span>
              ) : (
                <span className="ml-2 px-2 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-400">TARGET</span>
              )}
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-white mb-2">
              {valueLock ? formatNumber(valueLock.total_locked_beth) : '100B'} <span style={{ color: '#b8860b' }}>BETH</span>
            </h2>
            <p className="text-gray-400">
              {valueLock ? `$${formatNumber(valueLock.total_locked_usd)} USD` : 'Secured Collateral'} • Internal Security Reserve
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {valueLock?.allocations && Object.entries(valueLock.allocations).map(([key, alloc]) => (
              <div 
                key={key}
                className="p-6 rounded-lg text-center"
                style={{ background: 'rgba(13, 29, 53, 0.8)', border: '1px solid rgba(42, 61, 90, 0.5)' }}
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(184, 134, 11, 0.2)' }}>
                  {key === 'collateral_vault' && <Shield size={20} className="text-[#b8860b]" />}
                  {key === 'treasury_reserve' && <Lock size={20} className="text-[#b8860b]" />}
                  {key === 'liquidity_pool' && <TrendingUp size={20} className="text-[#b8860b]" />}
                </div>
                <p className="text-2xl font-light text-white mb-1">{formatNumber(alloc.amount)} BETH</p>
                <p className="text-sm text-[#b8860b] mb-1">{alloc.name}</p>
                <p className="text-xs text-gray-500">{alloc.purpose}</p>
              </div>
            ))}
            
            {/* Fallback if API not loaded */}
            {!valueLock && (
              <>
                <div className="p-6 rounded-lg text-center" style={{ background: 'rgba(13, 29, 53, 0.8)', border: '1px solid rgba(42, 61, 90, 0.5)' }}>
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(184, 134, 11, 0.2)' }}>
                    <Shield size={20} className="text-[#b8860b]" />
                  </div>
                  <p className="text-2xl font-light text-white mb-1">50B BETH</p>
                  <p className="text-sm text-[#b8860b] mb-1">Collateral Vault</p>
                  <p className="text-xs text-gray-500">Credit facility backing</p>
                </div>
                <div className="p-6 rounded-lg text-center" style={{ background: 'rgba(13, 29, 53, 0.8)', border: '1px solid rgba(42, 61, 90, 0.5)' }}>
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(184, 134, 11, 0.2)' }}>
                    <Lock size={20} className="text-[#b8860b]" />
                  </div>
                  <p className="text-2xl font-light text-white mb-1">30B BETH</p>
                  <p className="text-sm text-[#b8860b] mb-1">Treasury Reserve</p>
                  <p className="text-xs text-gray-500">Platform operational reserves</p>
                </div>
                <div className="p-6 rounded-lg text-center" style={{ background: 'rgba(13, 29, 53, 0.8)', border: '1px solid rgba(42, 61, 90, 0.5)' }}>
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(184, 134, 11, 0.2)' }}>
                    <TrendingUp size={20} className="text-[#b8860b]" />
                  </div>
                  <p className="text-2xl font-light text-white mb-1">20B BETH</p>
                  <p className="text-sm text-[#b8860b] mb-1">Platform Reserve</p>
                  <p className="text-xs text-gray-500">Operational reserves</p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(184, 134, 11, 0.3), transparent)' }} />
      </div>

      {/* Features */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-widest mb-4" style={{ color: '#b8860b' }}>PLATFORM CAPABILITIES</p>
            <h2 className="text-3xl font-light text-white">Infrastructure Features</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Credit Enhancement',
                description: 'Generate credit enhancement reports and Private Accord & Satisfaction agreements backed by internal BETH collateral.',
                icon: '📋'
              },
              {
                title: 'Non-Custodial Execution',
                description: 'User-signed transactions with no discretionary authority. You retain full custody and control of your assets.',
                icon: '🔐'
              },
              {
                title: 'Transparent Pricing',
                description: 'Fixed BETH pricing at $1.00 USD per platform credit. Clean, predictable, no volatility.',
                icon: '📊'
              },
              {
                title: 'Compliance Framework',
                description: 'Reg D 506(b) compliant structure with full accredited investor verification and required disclosures.',
                icon: '⚖️'
              },
              {
                title: 'Treasury Management',
                description: 'Internal credit system for platform access. No DEX dependency, no speculation, clean SaaS model.',
                icon: '🏛️'
              },
              {
                title: 'Smart Contract Security',
                description: 'Deployed on Ethereum mainnet with verified contracts. Automated execution per predefined rules.',
                icon: '🔗'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="p-8"
                style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}
              >
                <div className="text-2xl mb-4">{feature.icon}</div>
                <h3 className="text-white text-lg font-medium mb-3">{feature.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#6c757d' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-16 px-6" style={{ background: '#0d1117' }}>
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs tracking-widest mb-6" style={{ color: '#b8860b' }}>IMPORTANT NOTICE</p>
          <p className="text-sm leading-relaxed" style={{ color: '#6c757d' }}>
            Participation is limited to accredited investors as defined by SEC Rule 501 of Regulation D. 
            No offer or solicitation is made to the public. Digital assets involve significant risk, including 
            the potential loss of principal. The BETH token is a utility token used solely as collateral and 
            an accounting unit—it does not represent ownership, profit rights, or claims on revenues.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-light text-white mb-6">Begin Your Application</h2>
          <p className="text-gray-400 mb-8">
            Accredited investors may request access to the platform. Verification and 
            participation agreement required.
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="px-10 py-4 text-sm font-medium tracking-wider"
            style={{ 
              background: 'linear-gradient(180deg, #b8860b 0%, #9a7209 100%)',
              color: '#0a1628',
              border: 'none',
              borderRadius: '2px'
            }}
          >
            APPLY FOR ACCESS
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12 px-6" style={{ borderColor: 'rgba(184, 134, 11, 0.1)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #b8860b 0%, #8b6914 100%)' }}>
                <span className="text-[#0a1628] font-bold">B</span>
              </div>
              <span className="text-white text-sm tracking-wide">BETHEREUM</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="/whitepaper" className="text-xs" style={{ color: '#6c757d' }}>White Paper</a>
              <a href="/terms" className="text-xs" style={{ color: '#6c757d' }}>Terms</a>
              <a href="/privacy" className="text-xs" style={{ color: '#6c757d' }}>Privacy</a>
              <a href="/compliance" className="text-xs" style={{ color: '#6c757d' }}>Compliance</a>
            </div>
          </div>
          <div className="flex items-center justify-between pt-8 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
            <p className="text-xs" style={{ color: '#6c757d' }}>
              © 2026 Bethereum. All rights reserved.
            </p>
            <p className="text-xs" style={{ color: '#6c757d' }}>
              Accredited Investors Only • Reg D 506(b) Exempt
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageJP;
